import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Eye, Phone, Clock, CreditCard, Banknote, Trash2, ChevronDown, Building2, CheckCircle, XCircle, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  preparing: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  ready: 'bg-green-500/20 text-green-400 border-green-500/50',
  delivered: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/50',
};

const paymentStatusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  paid: 'bg-green-500/20 text-green-400 border-green-500/50',
  failed: 'bg-red-500/20 text-red-400 border-red-500/50',
};

const allStatuses = ['pending', 'preparing', 'ready', 'delivered', 'cancelled'];

const paymentMethodIcons = {
  card: CreditCard,
  cash: Banknote,
  transfer: Building2,
  whatsapp: Phone,
};

export default function AdminOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusDropdown, setStatusDropdown] = useState(null);
  const [paymentDropdown, setPaymentDropdown] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders?status=${statusFilter}`, { headers });
      setOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter]);

  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;
    let mounted = true;
    const ws = new WebSocket(`${wsUrl}/api/ws`);
    const pingInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) try { ws.send('ping'); } catch (_) {}
    }, 15000);
    ws.onmessage = (event) => {
      if (!mounted || event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_order' || data.type === 'order_status') fetchOrders();
      } catch (_) {}
    };
    ws.onerror = () => { clearInterval(pingInterval); ws?.close(); };
    ws.onclose = () => clearInterval(pingInterval);
    return () => { mounted = false; clearInterval(pingInterval); ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, { headers });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
    setStatusDropdown(null);
  };

  const updatePaymentStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { payment_status: newStatus }, { headers });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, payment_status: newStatus } : o));
      toast.success(`Payment ${newStatus === 'paid' ? 'confirmed' : 'marked as ' + newStatus}`);
    } catch (error) {
      toast.error('Failed to update payment status');
    }
    setPaymentDropdown(null);
  };

  const deleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Delete order #${orderNumber}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`, { headers });
      setOrders(prev => prev.filter(o => o.id !== orderId));
      toast.success('Order deleted');
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const getTimeSince = (dateStr) => {
    const diffMins = Math.floor((new Date() - new Date(dateStr)) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <AdminLayout title="Orders" subtitle="Track and manage all customer orders">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Orders" subtitle="Track and manage all customer orders">
      <div data-testid="admin-orders">
        {/* Header with export */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full sm:w-auto">
            <TabsList className="bg-card border border-white/10 flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="all" className="data-[state=active]:bg-fire-500 text-xs sm:text-sm">All</TabsTrigger>
              <TabsTrigger value="pending" className="data-[state=active]:bg-fire-500 text-xs sm:text-sm">Pending</TabsTrigger>
              <TabsTrigger value="preparing" className="data-[state=active]:bg-fire-500 text-xs sm:text-sm">Preparing</TabsTrigger>
              <TabsTrigger value="ready" className="data-[state=active]:bg-fire-500 text-xs sm:text-sm">Ready</TabsTrigger>
              <TabsTrigger value="delivered" className="data-[state=active]:bg-fire-500 text-xs sm:text-sm">Delivered</TabsTrigger>
              <TabsTrigger value="cancelled" className="data-[state=active]:bg-fire-500 text-xs sm:text-sm">Cancelled</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            onClick={() => {
              const link = document.createElement('a');
              link.href = `${API}/orders/export/csv`;
              link.setAttribute('download', '');
              // Need auth - use fetch
              fetch(`${API}/orders/export/csv`, { headers })
                .then(res => res.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  link.href = url;
                  link.download = `sizzlebox_orders.csv`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  toast.success('Orders exported!');
                })
                .catch(() => toast.error('Export failed'));
            }}
            className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
            data-testid="export-orders-btn"
          >
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
        </div>

        {/* Desktop Table (hidden on mobile) */}
        <div className="hidden lg:block bg-card rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/50">
                <tr>
                  <th className="text-left text-gray-400 text-sm font-medium px-4 py-3">Order</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-4 py-3">Customer</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-4 py-3">Time</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-4 py-3">Amount</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-4 py-3">Payment</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-4 py-3">Status</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => {
                  const PaymentIcon = paymentMethodIcons[order.payment_method] || CreditCard;
                  return (
                    <tr key={order.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`order-row-${order.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-fire-500 font-mono font-bold text-sm">#{order.order_number}</p>
                        <p className="text-gray-500 text-xs">{order.items.length} items</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white font-medium text-sm">{order.customer_name}</p>
                        <p className="text-gray-400 text-xs">{order.customer_phone}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white text-sm">{getTimeSince(order.created_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white font-bold text-sm">₦{order.total.toLocaleString()}</p>
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <PaymentIcon className="w-3 h-3" />
                          <span className="capitalize">{order.payment_method}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {/* Payment status with confirm/reject */}
                        <div className="relative">
                          <button
                            onClick={() => setPaymentDropdown(paymentDropdown === order.id ? null : order.id)}
                            className="flex items-center gap-1"
                            data-testid={`payment-btn-${order.id}`}
                          >
                            <Badge className={`${paymentStatusColors[order.payment_status] || paymentStatusColors.pending} border capitalize cursor-pointer text-xs`}>
                              {order.payment_status || 'pending'}
                            </Badge>
                            <ChevronDown className="w-3 h-3 text-gray-500" />
                          </button>
                          {paymentDropdown === order.id && (
                            <div className="absolute left-0 top-full mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-20 min-w-[160px] py-1">
                              <button onClick={() => updatePaymentStatus(order.id, 'confirmed')} className="w-full text-left px-3 py-2 text-sm text-green-400 hover:bg-white/10 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4" /> Confirm Paid
                              </button>
                              <button onClick={() => updatePaymentStatus(order.id, 'pending')} className="w-full text-left px-3 py-2 text-sm text-yellow-400 hover:bg-white/10 flex items-center gap-2">
                                <Clock className="w-4 h-4" /> Mark Pending
                              </button>
                              <button onClick={() => updatePaymentStatus(order.id, 'declined')} className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-white/10 flex items-center gap-2">
                                <XCircle className="w-4 h-4" /> Mark Declined
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative">
                          <button onClick={() => setStatusDropdown(statusDropdown === order.id ? null : order.id)} className="flex items-center gap-1" data-testid={`status-btn-${order.id}`}>
                            <Badge className={`${statusColors[order.status]} border capitalize cursor-pointer text-xs`}>{order.status}</Badge>
                            <ChevronDown className="w-3 h-3 text-gray-500" />
                          </button>
                          {statusDropdown === order.id && (
                            <div className="absolute left-0 top-full mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-20 min-w-[140px] py-1">
                              {allStatuses.map(s => (
                                <button key={s} onClick={() => updateStatus(order.id, s)} className={`w-full text-left px-3 py-2 text-sm capitalize hover:bg-white/10 ${order.status === s ? 'text-fire-500 font-bold' : 'text-gray-300'}`}>{s}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Link to={`/admin/orders/${order.id}`}><Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2"><Eye className="w-4 h-4" /></Button></Link>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-400 h-8 px-2" onClick={() => deleteOrder(order.id, order.order_number)} data-testid={`delete-order-${order.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile/Tablet Card Layout */}
        <div className="lg:hidden space-y-3">
          {orders.map(order => {
            const PaymentIcon = paymentMethodIcons[order.payment_method] || CreditCard;
            return (
              <div key={order.id} className="bg-card rounded-xl border border-white/10 p-4" data-testid={`order-card-${order.id}`}>
                {/* Top row: Order number + Amount */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-fire-500 font-mono font-bold text-sm">#{order.order_number}</p>
                    <p className="text-gray-500 text-xs">{getTimeSince(order.created_at)} - {order.items.length} items</p>
                  </div>
                  <p className="text-white font-bold text-lg">₦{order.total.toLocaleString()}</p>
                </div>

                {/* Customer */}
                <div className="flex items-center gap-2 mb-3 text-sm">
                  <span className="text-gray-400">Customer:</span>
                  <span className="text-white">{order.customer_name}</span>
                  <span className="text-gray-500 text-xs">({order.customer_phone})</span>
                </div>

                {/* Status badges row */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {/* Order Status */}
                  <div className="relative">
                    <button onClick={() => setStatusDropdown(statusDropdown === order.id ? null : order.id)} className="flex items-center gap-1">
                      <Badge className={`${statusColors[order.status]} border capitalize text-xs`}>{order.status}</Badge>
                      <ChevronDown className="w-3 h-3 text-gray-500" />
                    </button>
                    {statusDropdown === order.id && (
                      <div className="absolute left-0 top-full mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-20 min-w-[140px] py-1">
                        {allStatuses.map(s => (
                          <button key={s} onClick={() => updateStatus(order.id, s)} className={`w-full text-left px-3 py-2 text-sm capitalize hover:bg-white/10 ${order.status === s ? 'text-fire-500 font-bold' : 'text-gray-300'}`}>{s}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Payment Status */}
                  <div className="relative">
                    <button onClick={() => setPaymentDropdown(paymentDropdown === order.id ? null : order.id)} className="flex items-center gap-1">
                      <Badge className={`${paymentStatusColors[order.payment_status] || paymentStatusColors.pending} border capitalize text-xs`}>
                        <PaymentIcon className="w-3 h-3 mr-1" />{order.payment_status || 'pending'}
                      </Badge>
                      <ChevronDown className="w-3 h-3 text-gray-500" />
                    </button>
                    {paymentDropdown === order.id && (
                      <div className="absolute left-0 top-full mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-20 min-w-[160px] py-1">
                        <button onClick={() => updatePaymentStatus(order.id, 'confirmed')} className="w-full text-left px-3 py-2 text-sm text-green-400 hover:bg-white/10 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Confirm Paid</button>
                        <button onClick={() => updatePaymentStatus(order.id, 'pending')} className="w-full text-left px-3 py-2 text-sm text-yellow-400 hover:bg-white/10 flex items-center gap-2"><Clock className="w-4 h-4" /> Mark Pending</button>
                        <button onClick={() => updatePaymentStatus(order.id, 'declined')} className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-white/10 flex items-center gap-2"><XCircle className="w-4 h-4" /> Mark Declined</button>
                      </div>
                    )}
                  </div>

                  <span className="text-gray-500 text-xs capitalize flex items-center gap-1"><PaymentIcon className="w-3 h-3" />{order.payment_method}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                  <Link to={`/admin/orders/${order.id}`} className="flex-1">
                    <Button size="sm" variant="outline" className="w-full border-white/10 text-white hover:bg-white/10 text-xs h-8"><Eye className="w-3 h-3 mr-1" /> View</Button>
                  </Link>
                  <Button size="sm" variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs h-8 px-3" onClick={() => deleteOrder(order.id, order.order_number)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {orders.length === 0 && (
          <div className="text-center py-12 text-gray-500">No orders found</div>
        )}
      </div>

      {/* Click outside to close dropdowns */}
      {(statusDropdown || paymentDropdown) && (
        <div className="fixed inset-0 z-10" onClick={() => { setStatusDropdown(null); setPaymentDropdown(null); }} />
      )}
    </AdminLayout>
  );
}
